'use client'

import { data, tag, user } from '@blotoutio/edgetag-sdk-js';
import { Cart, CartLineItem, Product, ProductVariant } from 'lib/bigcommerce/types';
import { absoluteUrl, tryParseMoney } from 'lib/utils';

type AddToCartProps = { cart: Cart, lineItem: CartLineItem, quantity: number };
export const addToCart = ({ cart, lineItem, quantity }: AddToCartProps) => {
  if (typeof window === 'undefined') return {};

  const salePrice = tryParseMoney((lineItem.cost.gpm ?? lineItem.cost.listPrice).amount);
  const value = salePrice ? (salePrice * quantity) : undefined;
  const valueNumber = Number(value)

    tag('AddToCart', {
        currency: lineItem.cost.listPrice.currencyCode ?? 'USD',
        value: valueNumber,
        contents: [lineItemToBlotoutItem({ lineItem, quantity })],
        checkoutUrl: cart.checkoutUrl
    });
  }

export const lead = (location: string) => {
    if (typeof window === 'undefined') return {};
    tag('Lead', { name: location });
}

export const userData = (email: string, city?: string) => {
  if (typeof window === 'undefined') return {};

  if (!city) {
    user('email', email);
  } else {
    data({
      email: email,
      city: city
    });
  }
}

export const viewItem = ({ product, sku }: { product?: Product, sku?: string }) => {
  if (typeof window === 'undefined') return {};
  if (!product) return {};

  const variant = product.variants.find(v => v.sku === sku) ?? product.variants.at(0);

  const gpm = getGPM(product, variant);
  const gpmAmount = tryParseMoney(gpm);

  tag('ViewContent', {
    currency: variant?.price.currencyCode ?? 'USD',
    value: gpmAmount,
    contents: [productToBlotoutItem({ product, variant })]
  })
}

type FunnelAddToCartProps = { product?: Product, sku?: string, quantity?: number, url?: string };
export const funnelAddToCart = ({ product, sku, quantity = 1, url }: FunnelAddToCartProps) => {
  if (typeof window === 'undefined') return {};
  if (!product) return {};

  const variant = product?.variants?.find(v => v.sku === sku) ?? product?.variants.at(0);
  const gpm = getGPM(product, variant);
  const amount = tryParseMoney(gpm);
  const value = amount ? (amount * quantity) : undefined;
  const valueNumber = Number(value);

  tag('AddToCart', {
    currency: variant?.price.currencyCode ?? 'USD',
    value: valueNumber,
    contents: [productToBlotoutItem({ product, variant, quantity })],
    checkoutUrl: url
  });
}

type FunnelPurchaseProps = { product?: Product, orderId?: number, sku?: string, quantity?: number };
export const funnelPurchase = ({ product, orderId, sku, quantity = 1 }: FunnelPurchaseProps) => {
  if (!orderId) return;
  if (!product) return {};

  const variant = product?.variants?.find(v => v.sku === sku) ?? product?.variants.at(0);
  const gpm = getGPM(product, variant);
  const amount = tryParseMoney(gpm);
  const value = amount ? (amount * quantity) : undefined;
  const valueNumber = Number(value);

  tag('Purchase', {
    currency: product.price.currencyCode ?? 'USD',
    value: valueNumber,
    orderId: orderId,
    eventId: orderId,
    contents: [productToBlotoutItem({ product, variant, quantity })],
  })
}

// Utility Functions

const getGPM = (product: Product, variant?: ProductVariant) => {
  const gpm = variant?.productMetafields?.find(m => m.key === 'gpm')?.value?.toString() ?? product?.productMetafields?.find(m => m.key === 'gpm')?.value?.toString();
  return gpm;
}
// Mapper Functions

type BlotoutItem = {
  id?: string;
  quantity?: number;
  item_price?: number;
  variantId?: string;
  title?: string;
  description?: string;
  brand?: string;
  category?: string;
  image?: string;
  url?: string;
};

type CartLineItemProps = { lineItem: CartLineItem, quantity?: number };
const lineItemToBlotoutItem = ({ lineItem, quantity }: CartLineItemProps): BlotoutItem => {
  const product = lineItem.merchandise.product;
  const variant = lineItem.merchandise.product.variants.find(v => lineItem.sku === v.sku);
  const salePrice = lineItem.cost.gpm ?? lineItem.cost.listPrice;
  const salePriceNumber = Number(salePrice.amount);
  const categories = product.categories.map((category) => category.name).toString();

    return {
        id: lineItem.sku || lineItem.id,
        quantity: quantity ?? lineItem.quantity,
        item_price: salePriceNumber,
        variantId: variant?.sku,
        title: lineItem.merchandise.title,
        description: product.description,
        brand: product.brand,
        category: categories,
        image: product.images[0]?.url,
        url: absoluteUrl(product.handle)
    };
};

type ViewItemProps = { product: Product, variant?: ProductVariant, quantity?: number }
const productToBlotoutItem = ({ product, variant, quantity = 1 }: ViewItemProps): BlotoutItem => {
  const gpm = getGPM(product, variant);
  const gpmAmount = tryParseMoney(gpm);
  const categories = product.categories.map((category) => category.name).toString();

  return {
    id: product.sku,
    quantity: quantity,
    item_price: gpmAmount,
    variantId: variant?.sku,
    title: product.title,
    description: product.description,
    brand: product.brand,
    category: categories,
    image: product.images[0]?.url,
    url: absoluteUrl(product.handle)
  };
};
